import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  BookOpen,
  Clock,
  ChevronRight,
  Filter,
  Sparkles,
  TrendingUp,
  Leaf,
  Users,
  Newspaper,
  User,
  Lightbulb,
  Beaker,
  Landmark,
  CheckCircle2,
  RefreshCw,
  Trophy,
} from "lucide-react";
import Navbar from "../components/Navbar";
import { toast } from "sonner";

const categoryIcons = {
  economy: TrendingUp,
  polity: Landmark,
  science: Beaker,
  history: BookOpen,
  environment: Leaf,
  social: Users,
  editorial: Newspaper,
  story: BookOpen,
  biography: User,
  gk: Lightbulb,
};

const difficultyLabels = {
  1: "Beginner",
  2: "Intermediate",
  3: "Advanced",
  4: "Editorial",
};

const difficultyColors = {
  1: "diff-1",
  2: "diff-2",
  3: "diff-3",
  4: "diff-4",
};

const PracticeSelectPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, token } = useAuth();

  const [passages, setPassages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dailyChallenge, setDailyChallenge] = useState(null);
  const [userProgress, setUserProgress] = useState({});

  // Practice mode from URL params - these are MUTUALLY EXCLUSIVE
  const practiceMode = searchParams.get("practiceMode"); // "quick", "daily", "resume", or null
  const isQuickMode = practiceMode === "quick";
  const isDailyChallenge = practiceMode === "daily";
  const isResumeMode = practiceMode === "resume";

  // Default to "all" for mode filter, or use URL param
  const [selectedMode, setSelectedMode] = useState(searchParams.get("mode") || "all");
  const [selectedDifficulty, setSelectedDifficulty] = useState(searchParams.get("difficulty") || "");
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get("category") || "");

  useEffect(() => {
    // Show toast notifications for special modes
    if (isQuickMode) {
      toast.info("Quick Practice: Short passages with fewer questions", { duration: 3000 });
    } else if (isDailyChallenge) {
      toast.info("Daily Challenge: Test yourself with today's featured passage!", { duration: 3000 });
    } else if (isResumeMode) {
      toast.info("Continuing with your last practice settings", { duration: 2000 });
    }
  }, [practiceMode]);

  useEffect(() => {
    fetchCategories();
    fetchPassages();
    fetchUserProgress();
    // Only fetch daily challenge card if in DAILY CHALLENGE mode specifically
    if (isDailyChallenge) {
      fetchDailyChallenge();
    } else {
      setDailyChallenge(null);
    }
  }, [selectedMode, selectedDifficulty, selectedCategory, practiceMode]);

  const fetchUserProgress = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/passages/user-progress`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });
      setUserProgress(response.data.progress || {});
    } catch (error) {
      console.error("Error fetching user progress:", error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/passages/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchPassages = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      // Only add mode filter if not "all"
      if (selectedMode && selectedMode !== "all") params.append("mode", selectedMode);
      if (selectedDifficulty) params.append("difficulty", selectedDifficulty);
      if (selectedCategory) params.append("category", selectedCategory);

      const queryStr = params.toString();
      const url = queryStr ? `${API}/passages?${queryStr}` : `${API}/passages`;
      const response = await axios.get(url);
      
      let filteredPassages = response.data;
      
      // For Quick Practice mode, filter to passages with ≤4 questions
      if (isQuickMode) {
        filteredPassages = filteredPassages.filter(p => p.question_count <= 4);
      }
      
      // For Daily Challenge mode, limit to top passages
      if (isDailyChallenge) {
        filteredPassages = filteredPassages.slice(0, 5);
      }
      
      setPassages(filteredPassages);
    } catch (error) {
      console.error("Error fetching passages:", error);
      toast.error("Failed to load passages");
    } finally {
      setLoading(false);
    }
  };

  const fetchDailyChallenge = async () => {
    try {
      const response = await axios.get(`${API}/passages/daily/challenge?mode=${selectedMode}`);
      setDailyChallenge(response.data);
    } catch (error) {
      console.error("Error fetching daily challenge:", error);
    }
  };

  const handleFilterChange = (key, value) => {
    const params = new URLSearchParams(searchParams);
    if (value && value !== "all") {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    setSearchParams(params);

    if (key === "mode") setSelectedMode(value === "all" ? "all" : value);
    if (key === "difficulty") setSelectedDifficulty(value === "all" ? "" : value);
    if (key === "category") setSelectedCategory(value === "all" ? "" : value);
  };

  const modes = [
    { id: "all", name: "All Modes" },
    { id: "school", name: "School" },
    { id: "college", name: "College" },
    { id: "general", name: "General" },
    { id: "ssc", name: "SSC" },
    { id: "banking", name: "Banking" },
    { id: "upsc", name: "UPSC" },
  ];

  // Get mode badge info
  const getModeInfo = () => {
    if (isDailyChallenge) return { label: "Daily Challenge", color: "bg-amber-500", icon: "🏆", description: "Complete today's challenge to maintain your streak!" };
    if (isQuickMode) return { label: "Quick Practice", color: "bg-emerald-500", icon: "⚡", description: "Short passages with 4 or fewer questions" };
    if (isResumeMode) return { label: "Continue Practice", color: "bg-blue-500", icon: "▶️", description: "Continuing with your last practice settings" };
    return null;
  };

  const modeInfo = getModeInfo();

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header with Mode Badge */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900">
              {isDailyChallenge ? "Daily Challenge" : isQuickMode ? "Quick Practice" : "Practice Passages"}
            </h1>
            {modeInfo && (
              <Badge className={`${modeInfo.color} text-white px-3 py-1 text-sm gap-1`}>
                <span>{modeInfo.icon}</span>
                {modeInfo.label}
              </Badge>
            )}
          </div>
          <p className="text-slate-600">
            {modeInfo?.description || "Select a passage to start your reading comprehension practice"}
          </p>
        </div>

        {/* Daily Challenge Card */}
        {dailyChallenge && (
          <Card
            className="mb-8 border-sky-200 bg-gradient-to-r from-sky-50 to-white cursor-pointer card-hover"
            onClick={() => navigate(`/practice/${dailyChallenge.passage_id}`)}
            data-testid="daily-challenge-card"
          >
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-sky-500 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium text-sky-600">Daily Challenge</span>
                      <Badge variant="secondary" className="text-xs">
                        {dailyChallenge.questions?.length || 0} questions
                      </Badge>
                    </div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-1">{dailyChallenge.title}</h3>
                    <p className="text-slate-600 line-clamp-2">{dailyChallenge.content?.substring(0, 150)}...</p>
                  </div>
                </div>
                <ChevronRight className="w-6 h-6 text-slate-400 flex-shrink-0" />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <Card className="mb-8">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2 text-slate-600">
                <Filter className="w-4 h-4" />
                <span className="text-sm font-medium">Filters:</span>
              </div>

              <Select value={selectedMode} onValueChange={(v) => handleFilterChange("mode", v)}>
                <SelectTrigger className="w-40" data-testid="mode-filter">
                  <SelectValue placeholder="All Modes" />
                </SelectTrigger>
                <SelectContent>
                  {modes.map((mode) => (
                    <SelectItem key={mode.id} value={mode.id}>
                      {mode.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={selectedDifficulty} onValueChange={(v) => handleFilterChange("difficulty", v)}>
                <SelectTrigger className="w-44" data-testid="difficulty-filter">
                  <SelectValue placeholder="All Difficulties" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Difficulties</SelectItem>
                  {(categories.difficulties || []).map((d) => (
                    <SelectItem key={d.id} value={String(d.id)}>
                      {d.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={selectedCategory} onValueChange={(v) => handleFilterChange("category", v)}>
                <SelectTrigger className="w-44" data-testid="category-filter">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {(categories.categories || []).map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {(selectedMode !== "all" || selectedDifficulty || selectedCategory) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    handleFilterChange("mode", "all");
                    handleFilterChange("difficulty", "");
                    handleFilterChange("category", "");
                  }}
                  className="text-slate-500"
                  data-testid="clear-filters-btn"
                >
                  Clear filters
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Passages Grid */}
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-6">
                  <div className="h-4 bg-slate-200 rounded w-1/4 mb-4"></div>
                  <div className="h-6 bg-slate-200 rounded w-3/4 mb-3"></div>
                  <div className="h-4 bg-slate-200 rounded w-full mb-2"></div>
                  <div className="h-4 bg-slate-200 rounded w-2/3"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : passages.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No passages found</h3>
              <p className="text-slate-600 mb-4">Try adjusting your filters or check back later for new content.</p>
              <Button
                variant="outline"
                onClick={() => {
                  handleFilterChange("difficulty", "");
                  handleFilterChange("category", "");
                }}
              >
                Clear all filters
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {passages.map((passage, idx) => {
              const IconComponent = categoryIcons[passage.category] || BookOpen;
              const progress = userProgress[passage.passage_id];
              const isCompleted = progress?.is_completed;
              
              // Format last attempt date
              const formatDate = (dateStr) => {
                if (!dateStr) return "";
                const date = new Date(dateStr);
                const now = new Date();
                const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
                if (diffDays === 0) return "Today";
                if (diffDays === 1) return "Yesterday";
                if (diffDays < 7) return `${diffDays} days ago`;
                return date.toLocaleDateString();
              };
              
              return (
                <Card
                  key={passage.passage_id}
                  className={`cursor-pointer card-hover animate-fade-in ${
                    isCompleted ? "border-emerald-200 bg-emerald-50/30" : ""
                  }`}
                  onClick={() => navigate(`/practice/${passage.passage_id}`)}
                  style={{ animationDelay: `${idx * 0.05}s` }}
                  data-testid={`passage-card-${passage.passage_id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                        <IconComponent className="w-5 h-5 text-slate-600" />
                      </div>
                      <div className="flex items-center gap-2">
                        {isCompleted && (
                          <Badge className="bg-emerald-100 text-emerald-700 gap-1">
                            <CheckCircle2 className="w-3 h-3" />
                            Done
                          </Badge>
                        )}
                        <Badge className={difficultyColors[passage.difficulty]}>
                          {difficultyLabels[passage.difficulty]}
                        </Badge>
                      </div>
                    </div>

                    <h3 className="text-lg font-semibold text-slate-900 mb-2 line-clamp-2">{passage.title}</h3>

                    <p className="text-slate-600 text-sm line-clamp-3 mb-4">
                      {passage.content?.substring(0, 120)}...
                    </p>

                    {/* Completion stats for completed passages */}
                    {isCompleted && progress && (
                      <div className="flex items-center gap-3 mb-3 p-2 bg-emerald-50 rounded-lg text-sm">
                        <div className="flex items-center gap-1 text-emerald-700">
                          <Trophy className="w-3.5 h-3.5" />
                          <span className="font-medium">{Math.round(progress.best_accuracy)}%</span>
                        </div>
                        <span className="text-slate-400">•</span>
                        <span className="text-slate-600">
                          {progress.best_score}/{progress.best_total} correct
                        </span>
                        {progress.attempt_count > 1 && (
                          <>
                            <span className="text-slate-400">•</span>
                            <span className="text-slate-500">{progress.attempt_count} attempts</span>
                          </>
                        )}
                      </div>
                    )}

                    <div className="flex items-center justify-between text-sm text-slate-500">
                      <div className="flex items-center gap-1">
                        <BookOpen className="w-4 h-4" />
                        <span>{passage.question_count} questions</span>
                      </div>
                      {isCompleted ? (
                        <span className="text-xs text-slate-400">
                          {formatDate(progress.last_attempt_at)}
                        </span>
                      ) : (
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          <span>~{Math.max(5, passage.question_count * 2)} min</span>
                        </div>
                      )}
                    </div>

                    {/* Action button */}
                    {isCompleted ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full mt-3 text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/practice/${passage.passage_id}`);
                        }}
                        data-testid={`reattempt-btn-${passage.passage_id}`}
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Reattempt
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        className="w-full mt-3"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/practice/${passage.passage_id}`);
                        }}
                        data-testid={`start-btn-${passage.passage_id}`}
                      >
                        Start
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default PracticeSelectPage;
